import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Users2 } from "lucide-react";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { MultiSelect } from "@/components/forms/MultiSelect";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATE_LABEL = {
  pending: "awaiting an organizer",
  awaiting_owner: "awaiting the owner",
  declined: "declined",
};

/**
 * Groups in the CANONICAL Event / Resource editor (GRP-DEC-315/316).
 *
 * Two independent controls, deliberately not one:
 *   • **Groups** — a MULTI-select over the existing many-to-many association model. Each
 *     Group is authorized on its own by the server, so one Group that needs approval never
 *     drags the others into `pending` and a Group the user may not post to never blocks the
 *     rest. The selector only offers Groups the member may actually propose to; that is a
 *     courtesy, not the authorization.
 *   • **Visibility** — whether the object keeps its normal Startup Otter distribution or
 *     becomes members-only. Groups-only writes the server-side audience scope; the public
 *     surfaces then refuse it. Nothing is merely hidden in the UI, which is why switching a
 *     PUBLISHED public object asks for confirmation: its public links stop working.
 */
export function GroupAssociationFields({ contentType, value, onChange, visibility,
                                        onVisibilityChange, links = [], published,
                                        testId = "content-groups" }) {
  const [confirm, setConfirm] = React.useState(false);
  const { data } = useQuery({
    queryKey: ["groups-associable", contentType],
    queryFn: async () => (await api.get("/groups/associable",
      { params: { content_type: contentType } })).data,
    retry: false,
  });
  const groups = data?.groups || [];
  const options = groups.map((g) => ({
    value: g.id,
    label: g.needs_approval ? `${g.display_name} (needs approval)` : g.display_name,
  }));
  const selected = value || [];
  const pending = links.filter((l) => l.state !== "approved");

  const pickVisibility = (next) => {
    if (next === "groups" && visibility !== "groups" && published) {
      setConfirm(true);
      return;
    }
    onVisibilityChange(next);
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="space-y-1.5">
        <Label className="flex items-center gap-1.5">
          <Users2 className="h-4 w-4 text-muted-foreground" />Groups
        </Label>
        <MultiSelect options={options} value={selected} onChange={onChange}
                     placeholder={groups.length ? "Add to your groups…"
                       : "You're not in a group that accepts this yet"}
                     testId={`${testId}-select`} />
        <p className="text-xs text-muted-foreground">
          A {contentType} can belong to several groups. You stay its owner either way —
          groups only link to it.
        </p>
        {pending.length ? (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {pending.map((l) => (
              <Badge key={l.group_id} variant="secondary"
                     data-testid={`${testId}-state-${l.group_id}`}>
                {l.display_name}: {STATE_LABEL[l.state] || l.state}
              </Badge>
            ))}
          </div>
        ) : null}
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-[560]">Who can see it</legend>
        {[["public", "Everywhere it normally appears",
           `Keeps its normal Startup Otter visibility and also appears in the groups you chose.`],
          ["groups", "Selected groups only",
           `Only members of the groups you chose. Kept out of the public ${contentType} directory, search and links.`],
        ].map(([val, label, hint]) => (
          <label key={val}
                 className={`flex cursor-pointer items-start gap-2 rounded-lg border p-3 ${
                   (visibility || "public") === val ? "border-primary bg-secondary/40" : ""}`}>
            <input type="radio" name={`${testId}-visibility`} value={val} className="mt-1"
                   checked={(visibility || "public") === val}
                   onChange={() => pickVisibility(val)}
                   data-testid={`${testId}-visibility-${val}`} />
            <span className="text-sm">
              {label}
              <span className="block text-xs text-muted-foreground">{hint}</span>
            </span>
          </label>
        ))}
      </fieldset>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid={`${testId}-visibility-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Make this members only?</AlertDialogTitle>
            <AlertDialogDescription>
              This {contentType} is published, so it is reachable now. Switching to selected
              groups removes it from the public directory and search, and its existing
              public link will stop working for anyone outside those groups. You can switch
              back at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testId}-visibility-cancel`}>
              Keep it public
            </AlertDialogCancel>
            <AlertDialogAction data-testid={`${testId}-visibility-accept`}
                               onClick={() => onVisibilityChange("groups")}>
              Make it members only
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
